import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

export interface PromptDialogData {
  title: string;
  message: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

@Component({
  selector: 'app-prompt-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dialog-container" role="dialog" aria-modal="true">
      <h2 class="dialog-title" id="dialog-title">{{ data.title }}</h2>
      <p class="dialog-message" id="dialog-message">{{ data.message }}</p>
      <input
        type="text"
        class="dialog-input"
        [value]="value()"
        [placeholder]="data.placeholder || ''"
        (input)="onInput($event)" />
      <div class="dialog-actions">
        <button type="button" class="btn btn-secondary" (click)="onCancel()">
          {{ data.cancelLabel || 'Cancel' }}
        </button>
        <button type="button" class="btn btn-primary" (click)="onConfirm()">
          {{ data.confirmLabel || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: `
    .dialog-container {
      background: white;
      border-radius: 0.75rem;
      padding: 1.5rem;
      max-width: 28rem;
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .dialog-title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: oklch(0.2 0 0);
    }

    .dialog-message {
      margin: 0;
      font-size: 0.875rem;
      color: oklch(0.45 0 0);
      line-height: 1.5;
    }

    .dialog-input {
      padding: 0.625rem 0.75rem;
      border: 1px solid oklch(0.85 0 0);
      border-radius: 0.5rem;
      font-size: 0.875rem;
    }

    .dialog-input:focus {
      outline: none;
      border-color: oklch(0.5 0.2 250);
      box-shadow: 0 0 0 3px oklch(0.5 0.2 250 / 0.15);
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }

    .btn {
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: background-color 0.15s;
    }

    .btn-secondary {
      background: oklch(0.95 0 0);
      color: oklch(0.3 0 0);
    }

    .btn-secondary:hover {
      background: oklch(0.9 0 0);
    }

    .btn-primary {
      background: oklch(0.5 0.2 250);
      color: white;
    }

    .btn-primary:hover {
      background: oklch(0.45 0.2 250);
    }

    .btn:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: 2px;
    }
  `,
})
export class PromptDialogComponent {
  protected readonly dialogRef = inject(DialogRef<string | null>);
  protected readonly data = inject<PromptDialogData>(DIALOG_DATA);

  protected readonly value = signal('');

  protected onInput(event: Event): void {
    const nextValue = (event.target as HTMLInputElement).value;
    this.value.set(nextValue);
  }

  protected onConfirm(): void {
    this.dialogRef.close(this.value().trim());
  }

  protected onCancel(): void {
    this.dialogRef.close(null);
  }
}
